import { Box, Button, Chip, Paper, Radio, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import StarIcon from '@mui/icons-material/Star';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { Address } from '../../interfaces/Address/Address';
import { sharedColors } from '../../utils/Style';
import { useUserSummary } from '../../hooks/useUserSummary';
import { useThemeColor } from '../../hooks/useThemeColor';

type AddressCardProps = {
  address: Address;
  isDefault?: boolean;
  isBilling?: boolean;
  isShipping?: boolean;
  isSelected?: boolean;
  hideEditButton?: boolean;
  onDelete?: () => void;
  onSelect?: () => void;
};

export const AddressCard = ({
  address,
  isDefault = false,
  isBilling = false,
  isShipping = false,
  isSelected = false,
  hideEditButton = false,
  onDelete,
  onSelect,
}: AddressCardProps) => {
  const navigate = useNavigate();

  const { userSummary } = useUserSummary();

  const { primary, secondary } = useThemeColor();

  return (
    <Paper
      onClick={onSelect}
      elevation={3}
      sx={{
        p: 2.5,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        position: 'relative',
        justifyContent: 'center',
        cursor: onSelect ? 'pointer' : undefined,
      }}
    >
      {isDefault && (
        <Chip
          icon={<StarIcon fontSize='small' style={{ color: secondary }} />}
          label='Varsayılan adres'
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: primary,
            color: secondary,
            fontWeight: 600,
            fontSize: '12px',
          }}
        />
      )}

      {isBilling && (
        <Chip
          icon={
            <CreditCardIcon fontSize='small' style={{ color: secondary }} />
          }
          label='Fatura adresi'
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: primary,
            color: secondary,
            fontWeight: 600,
            fontSize: '12px',
          }}
        />
      )}

      {isShipping && (
        <Chip
          icon={
            <LocalShippingIcon fontSize='small' style={{ color: secondary }} />
          }
          label='Kargo adresi'
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: primary,
            color: secondary,
            fontWeight: 600,
            fontSize: '12px',
          }}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'center',
        }}
      >
        {!!onSelect && <Radio checked={isSelected} sx={{ p: 0 }} />}

        <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
          {address.firstName} {address.lastName}
        </Typography>
      </Box>

      {!!userSummary && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            my: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography sx={{ fontSize: '14px', color: sharedColors.gray5 }}>
              E-Posta Adresi
            </Typography>
            <Typography sx={{ fontSize: '14px' }}>
              {userSummary.email}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography sx={{ fontSize: '14px', color: sharedColors.gray5 }}>
              Telefon Numarası
            </Typography>
            <Typography sx={{ fontSize: '14px' }}>
              {userSummary.phone}
            </Typography>
          </Box>
        </Box>
      )}

      <Typography sx={{ fontSize: '14px' }}>{address.address1}</Typography>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, my: 1.5 }}>
        {!!address.city && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography sx={{ fontSize: '14px', color: sharedColors.gray5 }}>
              Şehir
            </Typography>
            <Typography sx={{ fontSize: '14px' }}>{address.city}</Typography>
          </Box>
        )}

        {!!address.state && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography sx={{ fontSize: '14px', color: sharedColors.gray5 }}>
              İlçe
            </Typography>
            <Typography sx={{ fontSize: '14px' }}>{address.state}</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
        {!hideEditButton && (
          <Button
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();

              navigate(`/hesabim/adres-duzenle/${address.id}`);
            }}
            sx={{
              backgroundColor: primary,
              color: secondary,
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
            }}
          >
            Düzenle
          </Button>
        )}

        {!!onDelete && (
          <Button
            onClick={(ev) => {
              ev.stopPropagation();
              ev.preventDefault();

              onDelete();
            }}
            sx={{
              backgroundColor: primary,
              color: secondary,
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
            }}
          >
            Sil
          </Button>
        )}
      </Box>
    </Paper>
  );
};
